// URL builders for the public read-side endpoints — kept separate from the
// write-side `api.ts` because these are GET URLs developers paste into a
// browser / curl, not fetch() calls the tester makes.
//
// Source of truth for the API shape:
//   https://poc-api.dev.refsix.com/api/tracked-matches

export const LIVE_VIEWER_BASE = "https://dev.grassrootslive.app/knvb-demo/match";
export const TRACKED_MATCHES_BASE = "https://poc-api.dev.refsix.com/tracked-matches";
export const SWAGGER_URL = "https://poc-api.dev.refsix.com/api/tracked-matches";

// Fixed for the KNVB hackathon environment (per the swagger description).
const DEFAULT_GROUP = "knvb-demo";

// How far either side of the match to widen the historical window — the
// /sse and /nonsse endpoints take a time window, not a matchId, so we
// bracket the match generously enough that it will fall inside.
const WINDOW_PAD_BEFORE_MS = 60 * 60 * 1000;       // 1 hour
const WINDOW_PAD_AFTER_MS = 4 * 60 * 60 * 1000;    // 4 hours

export interface WindowMatch {
  kickOffTime: number | null;
  endTime: number | null;
}

// Returns [from, to] epoch-ms bracketing the match. If the match hasn't
// started yet we pivot off "now" so the window is still meaningful.
export function buildTimeWindow(
  match: WindowMatch,
  now: number = Date.now()
): { from: number; to: number } {
  const pivot = match.kickOffTime ?? now;
  const from = pivot - WINDOW_PAD_BEFORE_MS;
  const to = (match.endTime ?? pivot) + WINDOW_PAD_AFTER_MS;
  return { from, to };
}

export function viewerUrl(matchId: string): string {
  return `${LIVE_VIEWER_BASE}/${encodeURIComponent(matchId)}`;
}

export function matchByIdUrl(matchId: string): string {
  return `${TRACKED_MATCHES_BASE}/match/${encodeURIComponent(matchId)}`;
}

export function nonsseUrl(window: { from: number; to: number }, group: string = DEFAULT_GROUP): string {
  return `${TRACKED_MATCHES_BASE}/nonsse/${encodeURIComponent(group)}/${window.from}/${window.to}`;
}

export function sseUrl(window: { from: number; to: number }, group: string = DEFAULT_GROUP): string {
  return `${TRACKED_MATCHES_BASE}/sse/${encodeURIComponent(group)}/${window.from}/${window.to}`;
}
